import {
  Slot,
  SlotListResponse,
  ApiSlotListResponse,
  SlotQueryParams,
  BulkSlotCreation,
  CreateSlotDto,
  UpdateSlotDto,
} from './slots.dto';
import { logging, API_BASE_URL } from '@/config/env';

// Custom logging function
const customLog = (...args: unknown[]) => {
  if (logging) {
    console.log(...args);
  }
};

class SlotsService {
  private baseUrl = API_BASE_URL;
  private logoutCallback: (() => void) | null = null;

  setLogoutCallback(callback: () => void) {
    this.logoutCallback = callback;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    customLog('=== API Request ===');
    customLog('URL:', url);
    customLog('Method:', options.method || 'GET');

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    customLog('Response status:', response.status);
    customLog('Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      customLog('Error response:', errorText);

      // Try to parse error response to check for session expired or get actual error message
      try {
        const errorData = JSON.parse(errorText);

        // Check for session expired
        if (
          errorData.status === false &&
          errorData.message === 'Invalid or expired admin session'
        ) {
          customLog('Session expired, triggering logout');
          if (this.logoutCallback) {
            this.logoutCallback();
          }
        }

        // Return the actual error message from the API
        const errorMessage =
          errorData.message ||
          errorData.error?.message ||
          `HTTP error! status: ${response.status}`;

        customLog('Throwing error with message:', errorMessage);
        throw new Error(errorMessage);
      } catch (parseError) {
        // If parsing fails (not our intentional error), throw generic error
        if (parseError instanceof SyntaxError) {
          customLog('JSON parse error:', parseError);
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        // Re-throw our intentional error
        throw parseError;
      }
    }

    const data = await response.json();
    customLog('Response data:', JSON.stringify(data, null, 2));

    // Check for status: false in response (API error)
    if (data.status === false) {
      customLog('API returned status: false, throwing error');

      // Check for session expired
      if (data.message === 'Invalid or expired admin session') {
        customLog('Session expired in response, triggering logout');
        if (this.logoutCallback) {
          this.logoutCallback();
        }
      }

      // Throw error with the actual message
      throw new Error(
        data.message || data.error?.message || 'API request failed'
      );
    }

    return data;
  }

  async getAllSlots(token: string): Promise<Slot[]> {
    customLog('API Service: getAllSlots called');

    const response = await this.request<ApiSlotListResponse>('/admin/slots', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.data) {
      customLog('API Service: Invalid response structure:', response);
      throw new Error('Invalid response format from server');
    }

    return Array.isArray(response.data)
      ? response.data
      : response.data.data || [];
  }

  async getSlots(
    token: string,
    params: SlotQueryParams = {}
  ): Promise<SlotListResponse> {
    customLog('API Service: getSlots called with params:', params);

    const queryString = new URLSearchParams();
    if (params.page) queryString.append('page', params.page.toString());
    if (params.limit) queryString.append('limit', params.limit.toString());
    if (params.search) queryString.append('search', params.search);
    if (params.status !== undefined)
      queryString.append('status', params.status.toString());
    if (params.startDate) queryString.append('startDate', params.startDate);
    if (params.endDate) queryString.append('endDate', params.endDate);
    if (params.dayOfWeek) queryString.append('dayOfWeek', params.dayOfWeek);
    if (params.sortBy) queryString.append('sortBy', params.sortBy);
    if (params.sortOrder) queryString.append('sortOrder', params.sortOrder);

    const endpoint = `/admin/slots${
      queryString.toString() ? `?${queryString.toString()}` : ''
    }`;

    customLog('API Service: Final endpoint:', endpoint);

    const response = await this.request<ApiSlotListResponse>(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    customLog('API Service: Parsed response structure:', {
      hasData: !!response.data,
      hasDataArray: Array.isArray(response.data)
        ? false
        : !!(response.data as any)?.data,
      hasMetadata: Array.isArray(response.data)
        ? false
        : !!(response.data as any)?.meta,
      dataLength: Array.isArray(response.data)
        ? response.data.length
        : (response.data as any)?.data?.length || 0,
    });

    // Handle both nested and flat response structures
    if (!response.data) {
      customLog('API Service: Warning - response.data is undefined');
      return {
        data: [],
        meta: { total: 0, page: 1, lastPage: 1, limit: 10 },
      };
    }

    // Check if response.data has the nested structure or is flat
    const responseData = response.data as any;

    // If response.data is an array (flat structure), use response metadata
    if (Array.isArray(responseData)) {
      const result = {
        data: responseData,
        meta: {
          total: (response as any).total || 0,
          page: (response as any).page || 1,
          lastPage: (response as any).lastPage || 1,
          limit: params.limit || 10,
        },
      };

      customLog('=== Flat Structure Result ===');
      customLog('result.data length:', result.data.length);
      customLog('result.meta:', result.meta);

      return result;
    }

    // Handle nested structure
    const result = {
      data: (responseData as any).data || responseData || [],
      meta: (responseData as any).meta || {
        total: 0,
        page: 1,
        lastPage: 1,
        limit: 10,
      },
    };

    customLog('=== Final Parsed Result ===');
    customLog('result.data length:', result.data.length);
    customLog('result.meta:', result.meta);

    return result;
  }

  async getSlotListByServiceId(
    token: string,
    id: number,
    params: SlotQueryParams = {}
  ): Promise<SlotListResponse> {
    customLog(
      'API Service: getSlotListByServiceId called with serviceId:',
      id,
      'params:',
      params
    );

    const queryString = new URLSearchParams();
    if (params.page) queryString.append('page', params.page.toString());
    if (params.limit) queryString.append('limit', params.limit.toString());

    const endpoint = `/admin/slots/${id}${
      queryString.toString() ? `?${queryString.toString()}` : ''
    }`;

    const response = await this.request<ApiSlotListResponse>(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    customLog('API Service: getSlotListByServiceId response:', response);

    if (!response.data) {
      customLog('API Service: Invalid response structure:', response);
      return {
        data: [],
        meta: { total: 0, page: 1, lastPage: 1, limit: 10 },
      };
    }

    // Handle both nested and flat response structures
    const responseData = response.data as any;

    // If response.data is an array (flat structure), use response metadata
    if (Array.isArray(responseData)) {
      const result = {
        data: responseData,
        meta: {
          total: (response as any).total || responseData.length,
          page: (response as any).page || 1,
          lastPage: (response as any).lastPage || 1,
          limit: params.limit || 10,
        },
      };

      customLog('=== Flat Structure Result ===');
      customLog('result.data length:', result.data.length);
      customLog('result.meta:', result.meta);

      return result;
    }

    // Handle nested structure
    const result = {
      data: (responseData as any).data || responseData || [],
      meta: (responseData as any).meta || {
        total: 0,
        page: 1,
        lastPage: 1,
        limit: 10,
      },
    };

    customLog('=== Final Parsed Result ===');
    customLog('result.data length:', result.data.length);
    customLog('result.meta:', result.meta);

    return result;
  }

  async createSlot(token: string, slotData: CreateSlotDto): Promise<Slot> {
    customLog('API Service: createSlot called with data:', slotData);

    const response = await this.request<ApiSlotListResponse>('/admin/slots', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(slotData),
    });

    if (!response.data) {
      customLog('API Service: Invalid response structure:', response);
      throw new Error('Invalid response format from server');
    }

    if (Array.isArray(response.data)) {
      return response.data[0] as Slot;
    } else {
      return response.data.data[0] as Slot;
    }
  }

  async updateSlot(
    token: string,
    id: number,
    slotData: UpdateSlotDto
  ): Promise<Slot> {
    customLog('API Service: updateSlot called with id:', id, 'data:', slotData);

    const response = await this.request<ApiSlotListResponse>(
      `/admin/slots/update-slot-by-date/${id}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(slotData),
      }
    );

    if (!response.data) {
      customLog('API Service: Invalid response structure:', response);
      throw new Error('Invalid response format from server');
    }

    // Handle different response structures
    if (Array.isArray(response.data)) {
      return response.data[0] as Slot;
    } else {
      const responseData = response.data as any;
      // If data is nested (response.data.data)
      if (responseData.data) {
        if (Array.isArray(responseData.data)) {
          return responseData.data[0] as Slot;
        } else {
          return responseData.data as Slot;
        }
      }
      // If data is the slot object directly
      return responseData as Slot;
    }
  }

  async getSlot(token: string, id: number): Promise<Slot> {
    customLog('API Service: getSlot called with id:', id);

    const response = await this.request<ApiSlotListResponse>(
      `/admin/slots/date-range/${id}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.data) {
      customLog('API Service: Invalid response structure:', response);
      throw new Error('Invalid response format from server');
    }

    // Handle different response structures
    if (Array.isArray(response.data)) {
      return response.data[0] as Slot;
    } else {
      const responseData = response.data as any;
      // If data is nested (response.data.data)
      if (responseData.data) {
        if (Array.isArray(responseData.data)) {
          return responseData.data[0] as Slot;
        } else {
          return responseData.data as Slot;
        }
      }
      // If data is the slot object directly
      return responseData as Slot;
    }
  }

  async deleteSlot(token: string, id: number): Promise<void> {
    customLog('API Service: deleteSlot called with id:', id);

    await this.request<ApiSlotListResponse>(`/admin/slots/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
  async deleteSlotByDate(token: string, id: number): Promise<void> {
    customLog('API Service: deleteSlot called with id:', id);

    await this.request<ApiSlotListResponse>(`/admin/slots/slot-by-date/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async createBulkSlots(
    token: string,
    bulkData: BulkSlotCreation
  ): Promise<Slot[]> {
    customLog('API Service: createBulkSlots called with data:', bulkData);

    const response = await this.request<ApiSlotListResponse>(
      '/admin/slots/bulk',
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(bulkData),
      }
    );

    if (!response.data) {
      customLog('API Service: Invalid response structure:', response);
      throw new Error('Invalid response format from server');
    }

    return Array.isArray(response.data)
      ? response.data
      : response.data.data || [];
  }

  /**
   * Fetch available booking slots for a specific date and service
   * This uses the public API endpoint: /api/v1/slots?date=...&serviceId=...
   */
  async getAvailableSlots(
    date: string,
    serviceId: number,
    admin: number = 1
  ): Promise<any[]> {
    customLog(
      'API Service: getAvailableSlots called with date:',
      date,
      'serviceId:',
      serviceId
    );

    const queryString = new URLSearchParams();
    queryString.append('date', date);
    queryString.append('serviceId', serviceId.toString());
    queryString.append('admin', admin.toString());

    const endpoint = `/api/v1/slots?${queryString.toString()}`;
    const url = `${this.baseUrl}${endpoint}`;

    customLog('Fetching available slots from:', url);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      customLog('Response status:', response.status);
      customLog('Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        customLog('Error response:', errorText);

        try {
          const errorData = JSON.parse(errorText);
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`
          );
        } catch (parseError) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      const data = await response.json();
      customLog('Available slots response:', data);

      // Handle different response structures
      // Expected: { status: true, data: ['09:00', '10:00', ...] } or { data: ['09:00', '10:00', ...] }
      if (data.data && Array.isArray(data.data)) {
        return data.data;
      }
      if (Array.isArray(data)) {
        return data;
      }

      return [];
    } catch (error) {
      customLog('Error fetching available slots:', error);
      throw error instanceof Error
        ? error
        : new Error('Failed to fetch available slots');
    }
  }

  getDuration(startTime: string, endTime: string): string {
    const [startH, startM, startS = 0] = startTime.split(':').map(Number);
    const [endH, endM, endS = 0] = endTime.split(':').map(Number);

    const startDate = new Date(0, 0, 0, startH, startM, startS);
    const endDate = new Date(0, 0, 0, endH, endM, endS);

    let diff = (endDate.getTime() - startDate.getTime()) / 1000 / 60; // in minutes

    if (diff < 0) diff += 24 * 60; // handle overnight slots (e.g., 11PM → 1AM)

    const hours = Math.floor(diff / 60);
    const minutes = Math.floor(diff % 60);

    if (hours === 0 && minutes === 0) return '0 min';
    if (hours === 0) return `${minutes} min`;
    if (minutes === 0) return `${hours} hr`;
    return `${hours} hr ${minutes} min`;
  }
}

export const slotsService = new SlotsService();
